import depsloader from "../../../depsloader";

const REQUEST_TIMEOUT_MS = 20000;
const DISTRICT_TYPES = ["district", "locality"];

function mapsRuntime() {
  return (typeof window !== "undefined" && window.ScreenworkMapsRuntime) || {};
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestSeq = 0;
  }

  cancelAutocomplete() {
    // invalidiert auch Antworten, die bereits unterwegs sind
    this.requestSeq++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    if (p.length === 0 && g.name) {
      p.push(g.name);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      return null;
    }

    // Feature nicht mutieren
    const p = Object.assign({}, l.properties || {});
    const type = p.type || "";
    const geoaddress = {};

    if (type === "street" || (!type && p.osm_key === "highway")) {
      if (!p.street && p.name) {
        p.street = p.name;
      }
    }

    if (p.street) {
      geoaddress["street"] = p.street;
      if (p.housenumber) {
        geoaddress["number"] = p.housenumber;
      }
    }

    if (p.postcode) {
      geoaddress["zip"] = p.postcode;
    }

    if (type === "city") {
      geoaddress["city"] = p.city || p.name;
    } else if (p.city) {
      geoaddress["city"] = p.city;
    }

    if (p.district) {
      geoaddress["district"] = p.district;
    } else if (DISTRICT_TYPES.indexOf(type) !== -1 && p.name && p.name !== geoaddress["city"]) {
      geoaddress["district"] = p.name;
    } else if (!type && p.name && p.name !== p.city) {
      // Legacy-Fallback für Antworten ohne type
      if (p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb")) {
        geoaddress["district"] = p.name;
      }
    }

    if (geoaddress["district"] && geoaddress["district"] === geoaddress["city"]) {
      delete geoaddress["district"];
    }

    if (!geoaddress["city"] && !geoaddress["street"] && p.name) {
      // Bezeichnung erhalten, ohne sie als Stadt auszugeben
      geoaddress["name"] = p.name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  _request(url, params, cancelToken) {
    return this.axios.get(url, {
      params,
      cancelToken,
      timeout: REQUEST_TIMEOUT_MS
    });
  }

  /**
   * Liefert eine Trefferliste oder null, wenn die Anfrage abgebrochen
   * oder von einer neueren Suche überholt wurde.
   */
  autocomplete(q) {
    this.cancelAutocomplete();
    const seq = this.requestSeq;

    const runtime = mapsRuntime();
    const query = typeof q === "string" ? q.trim() : "";
    if (!runtime.geocoderKey || !runtime.geocoderUrl || !query) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", runtime.geocoderKey);

    return this._request(runtime.geocoderUrl, params, source.token).then(res => {
      if (seq !== this.requestSeq) {
        return null;
      }

      const results = [];
      if (res && res.data && Array.isArray(res.data.features)) {
        res.data.features.forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress && geoaddress.text) {
            results.push(geoaddress);
          }
        });
      }

      return results;
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.requestSeq) {
        return null;
      }
      console.log(thrown);
      return [];
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    const runtime = mapsRuntime();
    if (!runtime.geocoderKey || !runtime.reverseUrl) {
      return Promise.resolve(null);
    }

    const nlat = Number(lat);
    const nlon = Number(lng);
    if (!isFiniteNumber(nlat) || !isFiniteNumber(nlon) || nlat < -90 || nlat > 90 || nlon < -180 || nlon > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const params = new URLSearchParams();
    params.append("lat", String(nlat));
    params.append("lon", String(nlon));
    params.append("lang", "de");
    params.append("key", runtime.geocoderKey);

    return this._request(runtime.reverseUrl, params).then(res => {
      if (res && res.data && Array.isArray(res.data.features) && res.data.features.length > 0) {
        const geoaddress = this._geoaddress(res.data.features[0]);
        if (geoaddress) {
          return geoaddress;
        }
      }
      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
